import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { buildSelfServiceApiUrl, getSelfServiceBaseUrl, unwrapArray, unwrapObject } from './self-service-api.utils';
import { readJson, writeJson } from './self-service-storage.utils';
import { buildPublishEnvelope, dashboardLayoutsEnvelope } from './dashboard-layouts-envelope.utils';

export interface PublishedLayout {
  name: string;
  slug: string;
}

/** One published dashboard owned by the current user, as returned by the
 *  email-filtered `dynamic_filter` API (the same call the sidenav uses to list
 *  a user's own dashboards). `layoutJson` is the RENDERED layout only — it does
 *  NOT carry the builder's editable items on this branch. */
export interface OwnedDashboardLayout {
  name: string;
  slug: string;
  layoutJson: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class SelfServiceDashboardService {
  private static readonly DRAFT_STORAGE_KEY = 'dvm.selfService.dashboardDraft';
  private static readonly DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  constructor(private readonly http: HttpClient) {}

  static readonly DYNAMIC_DASHBOARD_NAME_KEY = 'dvm.dynamicDashboard.id';

  /**
   * Slugs of dashboards the current user OWNS, published here by the sidenav
   * after its email-filtered `dynamic_filter` fetch (loadDynamicDashboard).
   * The sidenav re-fetches this on every login, so it survives logout (which
   * clears localStorage). The published-dashboard view reads it to decide
   * whether the viewer is the creator (show Back-to-Builder + Share) or a
   * share-link recipient (read-only). Held on the root singleton so the two
   * components share it without a direct reference.
   */
  private readonly ownedDashboardSlugs = new Set<string>();

  /** Register a slug the current user owns (called per dashboard by the sidenav). */
  registerOwnedDashboardSlug(slug: string): void {
    const normalized = (slug || '').trim().toLowerCase();
    if (normalized) this.ownedDashboardSlugs.add(normalized);
  }

  /** True when the sidenav has listed this slug among the current user's own
   *  dashboards. Synchronous — reflects whatever the sidenav has fetched so far. */
  isOwnedDashboardSlug(slug: string): boolean {
    return this.ownedDashboardSlugs.has((slug || '').trim().toLowerCase());
  }

  /** Clear the owned set (e.g. on logout) so a different user in the same tab
   *  doesn't inherit the previous user's ownership. */
  clearOwnedDashboardSlugs(): void {
    this.ownedDashboardSlugs.clear();
  }

  publishLayoutToApi(
    layoutJson: Record<string, unknown>,
    emailId = '',
    dashboardName = '',
    /** The dashboard is already published: always send `update`, never `insert`. */
    forceUpdate = false,
  ): Observable<unknown> {
    try {
      if (dashboardName) {
        localStorage.setItem(SelfServiceDashboardService.DYNAMIC_DASHBOARD_NAME_KEY, dashboardName);
      }
    } catch { /* ignore */ }

    if (forceUpdate && !emailId) {
      console.warn('[self-service] update sent without an email — the row is matched on dashboard_id + an empty email_id.');
    }

    const post = (uuid: string | null): Observable<unknown> =>
      this.http.post(getSelfServiceBaseUrl(), buildPublishEnvelope(layoutJson, emailId, dashboardName, uuid, forceUpdate)).pipe(
        tap(() => this.invalidateOwnedRows()),
        catchError(() => of(null)),
      );

    // This was always `operation: 'insert'`, so re-publishing the SAME
    // dashboard from a different browser/laptop (or after localStorage was
    // cleared) had no memory of it already existing and appended yet another
    // duplicate `dashboard_layouts` row — the exact issue `pickNewest()`
    // upstream has to work around on every read. Look up whether this email
    // already owns a row for this dashboard_id FIRST (server-side truth, not
    // local state), and update that row instead when one exists.
    // Update = upsert on (dashboard_id, email_id): no uuid lookup needed.
    if (forceUpdate || !emailId || !dashboardName) return post(null);
    return this.findOwnedDashboardLayoutRecord(dashboardName, emailId).pipe(
      switchMap((existing) => post(existing?.uuid ?? null)),
    );
  }

  /**
   * Grant another user access to a dashboard by inserting a copy of its layout
   * under THEIR email in `dashboard_layouts`. Same insert contract as
   * publishLayoutToApi, but targeted at an arbitrary recipient and WITHOUT the
   * local "current dashboard" side effect (we are not changing what the sharer
   * is viewing). Resolves true on success, false on failure.
   */
  shareLayoutWithUser(
    layoutJson: Record<string, unknown>,
    emailId: string,
    dashboardName: string,
    role = 'User',
  ): Observable<boolean> {
    // `dashboard_id` is the shareable URL key (see getPublishedLayoutBySlug), so a
    // share MUST reuse the original dashboard's own id. Re-deriving it from the
    // display name put the recipient's copy on a DIFFERENT slug than the link the
    // sharer sends, and dropped the uniqueness suffix buildDashboardId appends —
    // so two same-named dashboards collided in the recipient's list.
    // Fall back to a SLUGIFIED name (never the raw name) for older layouts that
    // carry no `dashboardId`, so the value is always a valid slug.
    const dashboardId =
      String(layoutJson?.['dashboardId'] ?? '').trim() || this.slugify(dashboardName);
    const payload = dashboardLayoutsEnvelope('upsert', {
      email_id: emailId,
      role,
      layout_json: layoutJson,
      // The backend matches an existing row on these columns, so re-sharing
      // the same dashboard to the same person updates it instead of adding
      // a duplicate — no uuid lookup needed.
      businessKey: ['dashboard_id', 'email_id'],
      dashboard_id: dashboardId,
    });
    return this.http.post(getSelfServiceBaseUrl(), payload).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /** The layout load, the ownership check and the views rail all want the same
   *  `dashboard_layouts/dynamic_filter` rows and ask for them in the same tick —
   *  they used to make three identical calls. Only the IN-FLIGHT request is
   *  shared: `finalize` drops it as soon as it settles, so the next dashboard
   *  open fetches again and never renders a layout someone else has changed.
   *  (shareReplay alone cannot do this — once an http source completes it
   *  replays its buffer to every later subscriber instead of re-requesting.) */
  private ownedRowsInFlight: { email: string; rows$: Observable<any[]> } | null = null;

  invalidateOwnedRows(): void {
    this.ownedRowsInFlight = null;
  }

  private ownedRows(emailId: string): Observable<any[]> {
    const email = (emailId || '').trim();
    const base = getSelfServiceBaseUrl();
    if (!email || !base) return of([]);

    if (this.ownedRowsInFlight?.email === email) return this.ownedRowsInFlight.rows$;

    const rows$ = this.http
      .post<any[]>(`${base}/dashboard_layouts/dynamic_filter`, { email_id: [email] })
      .pipe(
        map((response) => (Array.isArray(response) ? response : [])),
        catchError(() => of([] as any[])),
        finalize(() => { if (this.ownedRowsInFlight?.rows$ === rows$) this.ownedRowsInFlight = null; }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.ownedRowsInFlight = { email, rows$ };
    return rows$;
  }

  /**
   * Fetch a single published dashboard's layout JSON by its name-slug, WITHOUT
   * filtering by the requesting user's email. This is what makes a shared link
   * work for *any* logged-in user: it does the email-independent `select` of all
   * `dashboard_layouts` (same query the sidenav's published list uses) and picks
   * the one whose name slugifies to the requested slug. Returns null if not found
   * or the API is unavailable.
   */
  getPublishedLayoutBySlug(slug: string, emailId = ''): Observable<Record<string, unknown> | null> {
    const target = (slug || '').trim().toLowerCase();
    if (!target) return of(null);
    const email = (emailId || '').trim();
    const base = getSelfServiceBaseUrl();

    const pickBySlug = (list: any[]): Record<string, unknown> | null => {
      const items = Array.isArray(list) ? list : [];
      // PREFERENCE: match by `dashboard_id` first — the sidenav builds each menu
      // link's URL slug from `dashboard_id` (nameToSlug(item.dashboard_id)), so
      // that is the authoritative key. Only if nothing matches by dashboard_id do
      // we fall back to `layout_json.name` (covers older dashboards whose slug was
      // derived from the display name). Matching name-first was the bug: it missed
      // dashboards whose stored name differs from their dashboard_id.
      let matches = items.filter(
        (item: any) => this.slugify(String(item?.dashboard_id ?? '')) === target,
      );
      if (matches.length === 0) {
        matches = items.filter(
          (item: any) => this.slugify(String(item?.layout_json?.name ?? '')) === target,
        );
      }
      // Publish/save is an `insert` (see publishLayoutToApi), so re-publishing a
      // dashboard APPENDS a new `dashboard_layouts` row with the SAME
      // dashboard_id — the API returns every historical version. Picking the
      // FIRST match returned the OLDEST (stale) copy, so a chart the user had
      // removed kept re-appearing. Pick the NEWEST instead: prefer the highest
      // `publishedAt`/`updatedAt` timestamp, and fall back to the LAST occurrence
      // (append-insert order → last = most recent), which also matches the
      // sidenav cache's last-write-wins behaviour.
      const match = this.pickNewest(matches);
      const layout = match?.layout_json;
      return layout && typeof layout === 'object' ? (layout as Record<string, unknown>) : null;
    };

    // Prefer the email-filtered `dynamic_filter` endpoint (the SAME call the
    // sidenav uses to list a user's own dashboards). On this backend the
    // email-independent `select` below returns an empty responseList
    // (COMMIT_ROLLBACK), so the dynamic_filter path is what actually resolves a
    // dashboard the current user owns/was shared. Falls back to the select for a
    // recipient whose email isn't available yet.
    const dynamicFilter$: Observable<Record<string, unknown> | null> =
      email && base
        ? this.ownedRows(email).pipe(
            map((rows) => pickBySlug(rows)),
            catchError(() => of(null)),
          )
        : of(null);

    const selectPayload = {
      requestData: { email_id: '', role: 'User', operation: 'select', layout_json: {} },
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
    const select$: Observable<Record<string, unknown> | null> = this.http
      .post<unknown>(getSelfServiceBaseUrl(), selectPayload)
      .pipe(
        map((response: any) => {
          const list: any[] = Array.isArray(response?.responseList)
            ? response.responseList
            : Array.isArray(response?.data)
              ? response.data
              : [];
          return pickBySlug(list);
        }),
        catchError(() => of(null)),
      );

    // Try dynamic_filter first; if it yields nothing, fall back to the select.
    return dynamicFilter$.pipe(
      switchMap((viaFilter) => (viaFilter ? of(viaFilter) : select$)),
    );
  }

  /**
   * True when the dashboard with this name-slug is owned by `emailId` — i.e. it
   * is returned by the email-filtered `dynamic_filter` query (the same call the
   * sidenav uses to list a user's own dashboards). This is the authoritative,
   * LOGOUT-PROOF ownership check: it hits the API with the current user's email
   * instead of relying on localStorage (which logout clears). Used to decide
   * whether the creator viewing their own dashboard on the shared
   * `/dynamic-dashboard/<slug>` route should still see the Share action.
   * Resolves false on empty inputs or API failure (fail closed → treated as a
   * recipient, i.e. read-only).
   */
  isDashboardOwnedByCurrentUser(slug: string, emailId: string): Observable<boolean> {
    const target = (slug || '').trim().toLowerCase();
    const email = (emailId || '').trim();
    if (!target || !email) return of(false);

    const base = getSelfServiceBaseUrl();
    if (!base) return of(false);

    return this.ownedRows(email).pipe(
      map((list) => list.some((item: any) => {
        const name = String(item?.dashboard_id ?? item?.layout_json?.name ?? '');
        return this.slugify(name) === target;
      })),
      catchError(() => of(false)),
    );
  }

  /**
   * List the CURRENT user's published dashboards from the email-filtered
   * `dashboard_layouts/dynamic_filter` API — the exact call the sidenav uses to
   * populate the left menu. Returned so the builder's "Saved dashboards" tab can
   * reflect what's actually published server-side (source of truth) rather than
   * only what's in this browser's localStorage.
   *
   * Each item's `layoutJson` is the RENDERED layout only (no editable builder
   * items on this branch), so callers that want to EDIT one must still fall back
   * to a local copy — this list is for display + open. Resolves to [] on empty
   * email or API failure.
   */
  fetchOwnedDashboardsFromApi(emailId: string): Observable<OwnedDashboardLayout[]> {
    const email = (emailId || '').trim();
    if (!email) return of([]);

    const base = getSelfServiceBaseUrl();
    if (!base) return of([]);

    return this.ownedRows(email)
      .pipe(
        map((list) => {
          return list
            .map((item: any) => {
              const layoutJson = item?.layout_json;
              if (!layoutJson || typeof layoutJson !== 'object') return null;
              const name = String(item?.dashboard_id ?? layoutJson?.name ?? '').trim();
              if (!name) return null;
              return { name, slug: this.slugify(name), layoutJson } as OwnedDashboardLayout;
            })
            .filter((v): v is OwnedDashboardLayout => v !== null);
        }),
        catchError(() => of([])),
      );
  }

  private slugify(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** Choose the most recent record from a set of same-slug `dashboard_layouts`
   *  rows (publish is an insert, so duplicates accumulate). Prefers the highest
   *  `publishedAt`/`updatedAt` timestamp (checked on the row and inside its
   *  `layout_json`); when none carry a timestamp, falls back to the LAST item —
   *  the natural append-insert order puts the newest row last. */
  private pickNewest(matches: any[]): any {
    if (!Array.isArray(matches) || matches.length === 0) return undefined;
    if (matches.length === 1) return matches[0];
    const recency = (item: any): number => {
      const raw =
        item?.publishedAt ?? item?.updatedAt ??
        item?.layout_json?.publishedAt ?? item?.layout_json?.updatedAt ?? '';
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isNaN(t) ? -1 : t;
    };
    // Reduce keeping the newest; ties (including no-timestamp, recency -1) keep
    // the LATER item so append-insert order wins.
    return matches.reduce((best, item) => (recency(item) >= recency(best) ? item : best));
  }

  /** First non-empty string among the candidates, trimmed. */
  private firstString(...candidates: unknown[]): string | null {
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
    return null;
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  /** Pull a record's uuid out of whichever field the backend actually used —
   *  same fallback chain as dashboard.component.ts's resolvePersistedLayoutUuid,
   *  reused here so `dashboard_layouts` publish/save calls can also tell insert
   *  from update. */
  private resolveRecordUuid(record: any): string | null {
    const direct = this.firstString(
      record?.uuid,
      record?.UUID,
      record?.layout_uuid,
      record?.layoutUuid,
      record?.requestData?.uuid,
      record?.data?.uuid,
    );
    if (direct) return direct;
    const stringId = typeof record?.id === 'string' ? record.id.trim() : '';
    return this.isUuidLike(stringId) ? stringId : null;
  }

  /**
   * Find the CURRENT user's existing `dashboard_layouts` row for a dashboard
   * name/slug (via the same email-filtered `dynamic_filter` call the sidenav and
   * getPublishedLayoutBySlug use), and return its uuid. Callers use this to
   * decide `operation: 'update'` + that uuid vs `operation: 'insert'` when
   * publishing — publish/save was always `insert`, so re-publishing an existing
   * dashboard appended a duplicate `dashboard_layouts` row every time (see
   * getPublishedLayoutBySlug's "pick newest" workaround). Resolves null when no
   * matching row is owned by this email (fresh dashboard → insert) or on
   * empty/failed lookup.
   */
  findOwnedDashboardLayoutRecord(nameOrSlug: string, emailId: string): Observable<{ uuid: string; raw: unknown } | null> {
    const target = this.slugify(nameOrSlug);
    const email = (emailId || '').trim();
    const base = getSelfServiceBaseUrl();
    if (!target || !email || !base) return of(null);

    return this.ownedRows(email).pipe(
      map((list) => {
        const matches = list.filter((item: any) => {
          const name = String(item?.dashboard_id ?? item?.layout_json?.name ?? '');
          return this.slugify(name) === target;
        });
        const match = this.pickNewest(matches);
        const uuid = match ? this.resolveRecordUuid(match) : null;
        return uuid ? { uuid, raw: match } : null;
      }),
      catchError(() => of(null)),
    );
  }

  listPublishedLayouts(): Observable<PublishedLayout[]> {
    const payload = {
      requestData: { email_id: '', role: 'User', operation: 'select', layout_json: {} },
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
    return this.http
      .post<unknown>(buildSelfServiceApiUrl(''), payload)
      .pipe(
        map((response: any) => {
          const list: any[] = Array.isArray(response?.responseList)
            ? response.responseList
            : Array.isArray(response?.data)
              ? response.data
              : [];
          return list
            .filter((item: any) => item?.layout_json?.name)
            .map((item: any) => {
              const name = String(item.layout_json.name);
              const slug = name
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9_-]+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              return { name, slug };
            });
        }),
        catchError(() => of([])),
      );
  }

  getDraft<T>(): Observable<T | null> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/dashboards/draft'))
      .pipe(
        map((response) => unwrapObject<T>(response)),
        catchError(() => of(this.readLocalDraft<T>())),
      );
  }

  saveDraft<T>(draft: T): Observable<T> {
    return this.http
      .put<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/dashboards/draft'), draft)
      .pipe(
        map((response) => unwrapObject<T>(response) || draft),
        catchError(() => {
          this.writeLocalDraft(draft);
          return of(draft);
        }),
      );
  }

  listDashboards<T>(): Observable<T[]> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/dashboards'))
      .pipe(
        map((response) => {
          const items = unwrapArray<T>(response, ['items', 'data', 'results', 'dashboards']);
          return items.length ? items : this.readLocalDashboards<T>();
        }),
        catchError(() => of(this.readLocalDashboards<T>())),
      );
  }

  getDashboard<T>(dashboardId: string): Observable<T | null> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl(`/api/dvm/self-service/dashboards/${encodeURIComponent(dashboardId)}`))
      .pipe(
        map((response) => unwrapObject<T>(response)),
        catchError(() => of(this.readLocalDashboards<T>().find((item: any) => item?.id === dashboardId) || null)),
      );
  }

  saveDashboard<T extends { id?: string }>(dashboard: T): Observable<T> {
    const dashboardId = (dashboard as any)?.id;
    const request = dashboardId
      ? this.http.put<unknown>(
          buildSelfServiceApiUrl(`/api/dvm/self-service/dashboards/${encodeURIComponent(dashboardId)}`),
          dashboard,
        )
      : this.http.post<unknown>(
          buildSelfServiceApiUrl('/api/dvm/self-service/dashboards'),
          dashboard,
        );

    return request.pipe(
      map((response) => {
        const saved = unwrapObject<Record<string, unknown>>(response) || {};
        return { ...dashboard, ...saved } as T;
      }),
      catchError(() => {
        const next = this.upsertLocalDashboard(dashboard as T & { id?: string });
        return of(next);
      }),
    );
  }

  deleteDashboard(dashboardId: string): Observable<void> {
    return this.http
      .delete<void>(buildSelfServiceApiUrl(`/api/dvm/self-service/dashboards/${encodeURIComponent(dashboardId)}`))
      .pipe(
        catchError(() => {
          this.writeLocalDashboards(this.readLocalDashboards().filter((item: any) => item?.id !== dashboardId));
          return of(void 0);
        }),
      );
  }

  publishDashboard(dashboardId: string): Observable<{ publishedAt?: string } | null> {
    return this.http
      .post<{ publishedAt?: string }>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/dashboards/${encodeURIComponent(dashboardId)}/publish`),
        {},
      )
      .pipe(
        map((response) => unwrapObject<{ publishedAt?: string }>(response) || response),
        catchError(() => of({ publishedAt: new Date().toISOString() })),
      );
  }

  unpublishDashboard(dashboardId: string): Observable<unknown> {
    return this.http
      .post(
        buildSelfServiceApiUrl(`/api/dvm/self-service/dashboards/${encodeURIComponent(dashboardId)}/unpublish`),
        {},
      )
      .pipe(catchError(() => of(null)));
  }

  replaceLocalDashboardCache<T>(dashboards: T[]): void {
    this.writeLocalDashboards(dashboards);
  }

  private readLocalDraft<T>(): T | null {
    // Reads compressed (new) and legacy plaintext (old) payloads transparently.
    return readJson<T | null>(SelfServiceDashboardService.DRAFT_STORAGE_KEY, null);
  }

  private writeLocalDraft<T>(draft: T): void {
    // Compresses before writing; best-effort (fallback cache only).
    writeJson(SelfServiceDashboardService.DRAFT_STORAGE_KEY, draft);
  }

  private readLocalDashboards<T>(): T[] {
    return readJson<T[]>(SelfServiceDashboardService.DASHBOARDS_STORAGE_KEY, []);
  }

  private writeLocalDashboards<T>(dashboards: T[]): void {
    writeJson(SelfServiceDashboardService.DASHBOARDS_STORAGE_KEY, dashboards);
  }

  private upsertLocalDashboard<T extends { id?: string }>(dashboard: T): T {
    const current = this.readLocalDashboards<T>();
    if (!dashboard.id) {
      const next = [dashboard, ...current];
      this.writeLocalDashboards(next);
      return dashboard;
    }
    const next = current.some((item) => item.id === dashboard.id)
      ? current.map((item) => (item.id === dashboard.id ? dashboard : item))
      : [dashboard, ...current];
    this.writeLocalDashboards(next);
    return dashboard;
  }
}
